// Third party imports
import { FaCircle } from "react-icons/fa";
import i18n from '../i18n/config';

export const SIDEBAR_WIDTH = 235;
export const SIDEBAR_WIDTH_COLLAPSED = 80;

export const DEFAULT_PAGE_SIZE = 50;
export const DEFAULT_PAGE_NUMBER = 0;
export const DEFAULT_DATAGRID_STYLE = {
  width: "100%",
  height: "500px",
  border: "none",
  fontSize: "0.75rem",
  minHeight: "200px",
  marginTop: "20px",
  marginBottom: "20px",
};

// Translation-aware status map
export const getStatusMap = () => ({
  // Changed to be smaller. They are now 6px instead of 10px. They look good like this too
  ACTIVE: {
    bg: "#EEF5F0",
    text: "#467E52",
    icon: <FaCircle style={{ fontSize: "6px", marginRight: "4px", color: "#467E52" }} />,
    label: i18n.t('constants:status.active'),
  },
  STANDBY: {
    bg: "#F7F7E8",
    text: "#B7791F",
    icon: <FaCircle style={{ fontSize: "6px", marginRight: "4px", color: "#B7791F" }} />,
    label: i18n.t('constants:status.standBy'),
  },
  NOTACTIVE: {
    bg: "#F7EDED",
    text: "#8C3C3C",
    icon: <FaCircle style={{ fontSize: "6px", marginRight: "4px", color: "#8C3C3C" }} />,
    label: i18n.t('constants:status.notActive'),
  },
});

// Backward compatibility - static version
export const STATUS_MAP = {
  ACTIVE: {
    bg: "#EEF5F0",
    text: "#467E52",
    icon: <FaCircle style={{ fontSize: "6px", marginRight: "4px", color: "#467E52" }} />,
    label: i18n.t('constants:status.active'),
  },
  STANDBY: {
    bg: "#F7F7E8",
    text: "#B7791F",
    icon: <FaCircle style={{ fontSize: "6px", marginRight: "4px", color: "#B7791F" }} />,
    label: i18n.t('constants:status.standBy'),
  },
  NOTACTIVE: {
    bg: "#F7EDED",
    text: "#8C3C3C",
    icon: <FaCircle style={{ fontSize: "6px", marginRight: "4px", color: "#8C3C3C" }} />,
    label: i18n.t('constants:status.notActive'),
  },
};

// Translation-aware company status map
export const getCompanyStatusMap = () => ({
  ACTIVE: {
    icon: <FaCircle style={{ marginRight: "5px", color: "#467E52" }} />,
    text: i18n.t('constants:status.active'),
  },
  STANDBY: {
    icon: <FaCircle style={{ marginRight: "5px", color: "#B7791F" }} />,
    text: i18n.t('constants:status.standBy'),
  },
  NOTACTIVE: {
    icon: <FaCircle style={{ marginRight: "5px", color: "#8C3C3C" }} />,
    text: i18n.t('constants:status.notActive'),
  }
});

// Backward compatibility - static version
export const companyStatusMap = {
  ACTIVE: {
    icon: <FaCircle style={{ marginRight: "5px", color: "#467E52" }} />,
    text: i18n.t('constants:status.active'),
  },
  STANDBY: {
    icon: <FaCircle style={{ marginRight: "5px", color: "#B7791F" }} />,
    text: i18n.t('constants:status.standBy'),
  },
  NOTACTIVE: {
    icon: <FaCircle style={{ marginRight: "5px", color: "#8C3C3C" }} />,
    text: i18n.t('constants:status.notActive'),
  }
};

// Type tag colors mapping
export const TYPES_MAP = {
  AAA: { bg: "#EEF5F0", text: "#467E52" },
  A: { bg: "#EDF2FF", text: "#4263EB" },
  B: { bg: "#F7EDED", text: "#8C3C3C" },
};

// Translation-aware board columns
export const getBoardColumns = (isMyBoard = false) => {
  return {
    columns: {
      SERVICES: {
        id: "SERVICES",
        title: i18n.t('constants:board.masterPlan'),
        taskIds: [],
        color: "#000",
      },
      TODO: {
        id: "TODO",
        title: i18n.t('constants:board.todo'),
        taskIds: [],
        color: "#247DFF",
      },
      IN_PROGRESS: {
        id: "IN_PROGRESS",
        title: i18n.t('constants:board.inProgress'),
        taskIds: [],
        color: "#FBBC05",
      },
      IN_REVIEW: {
        id: "IN_REVIEW",
        title: i18n.t('constants:board.inReview'),
        taskIds: [],
        color: "#F99E26",
      },
      TO_UPLOAD: {
        id: "TO_UPLOAD",
        title: i18n.t('constants:board.toUpload'),
        taskIds: [],
        color: "#DC8007",
      },
      DELIVERED: {
        id: "DELIVERED",
        title: i18n.t('constants:board.delivered'),
        taskIds: [],
        color: "#589E67",
      },
    },
    columnOrder: isMyBoard
      ? ["TODO", "IN_PROGRESS", "IN_REVIEW", "TO_UPLOAD"]
      : ["SERVICES", "TODO", "IN_PROGRESS", "IN_REVIEW", "TO_UPLOAD", "DELIVERED"],
  };
};

// Team board columns are data now: see the "boards" API (each board
// carries its own configurable columns).

// Subtask statuses (translation-aware via i18n.t)
export const STATUS_SUBTASKS = {
  TODO: {
    color: "#247DFF",
    label: i18n.t('constants:subtaskStatus.todo'),
    pastelColor: "#E3F2FD", // Light blue pastel background
    solidColor: "#1976D2", // Darker blue for text/borders
  },
  IN_PROGRESS: {
    color: "#FFBD2E",
    label: i18n.t('constants:subtaskStatus.inProgress'),
    pastelColor: "#FFF4E6", // Light orange pastel background
    solidColor: "#F57C00", // Darker orange for text/borders
  },
  IN_REVIEW: {
    color: "#FF6B00",
    label: i18n.t('constants:subtaskStatus.inReview'),
    pastelColor: "#FFF3E0", // Light amber pastel background
    solidColor: "#E65100", // Darker orange for text/borders
  },
  TO_UPLOAD: {
    color: "#DC8007",
    label: i18n.t('constants:subtaskStatus.toUpload'),
    pastelColor: "#FFF8E1", // Light yellow pastel background
    solidColor: "#F57C00", // Darker amber for text/borders
  },
  DELIVERED: {
    color: "#28CA42",
    label: i18n.t('constants:subtaskStatus.delivered'),
    pastelColor: "#E8F5E8", // Light green pastel background
    solidColor: "#2E7D32", // Darker green for text/borders
  },
};

export const supportedPlatforms = [
  "500px",
  "aboutme",
  "amazon",
  "android",
  "angellist",
  "apple",
  "behance",
  "bookmate",
  "codepen",
  "coub",
  "delicious",
  "deviantart",
  "digg",
  "dribbble",
  "dropbox",
  "ello",
  "envato",
  "etsy",
  "etsy-short",
  "evernote",
  "facebook",
  "favorite",
  "flikr",
  "foursquare",
  "github",
  "googledrive",
  "googleplay",
  "googleplus",
  "instagram",
  "jsfiddle",
  "kickstarter",
  "line",
  "line-icon",
  "linkedin",
  "livejournal",
  "mail",
  "map",
  "medium",
  "messenger",
  "ok",
  "periscope",
  "phone",
  "photobucket",
  "pinterest",
  "pinterest-light",
  "pocket",
  "producthunt",
  "quora",
  "reddit",
  "reddit-light",
  "rss",
  "send",
  "send-light",
  "shopify",
  "sketch",
  "skype",
  "slack",
  "slideshare",
  "snapchat",
  "stackoverflow",
  "stumbleupon",
  "swarm",
  "tripadvisor",
  "tumblr",
  "twitter",
  "upwork",
  "viadeo",
  "vimeo",
  "vk",
  "vscogrid",
  "web",
  "whatsapp",
  "windows",
  "wordpress",
  "xing",
  "yelp",
  "youtube",
  "youtube-play",
];

/**
 * Get status styling for consistent appearance across the app
 * @param {string} status - The status key (e.g., 'TODO', 'IN_PROGRESS')
 * @param {string} variant - The style variant ('pastel', 'solid', 'badge')
 * @returns {object} Style object with color, backgroundColor, etc.
 * (A third argument was once accepted for translated labels: the styling never uses labels, and
 * it pointed to a getStatusSubtasks() that does not exist, so it is ignored.)
 */
export const getStatusStyling = (status, variant = 'pastel') => {
  const statusKey = status?.toUpperCase();
  const statusConfig = STATUS_SUBTASKS[statusKey];

  if (!statusConfig) {
    return {
      color: "#757575",
      backgroundColor: "#F5F5F5",
      borderColor: "#E0E0E0",
    };
  }

  switch (variant) {
    case 'pastel':
      return {
        color: statusConfig.solidColor,
        backgroundColor: statusConfig.pastelColor,
        borderColor: statusConfig.color,
      };
    case 'solid':
      return {
        color: "#FFFFFF",
        backgroundColor: statusConfig.solidColor,
        borderColor: statusConfig.solidColor,
      };
    case 'badge':
      return {
        color: statusConfig.color,
        backgroundColor: `${statusConfig.color}20`, // 20% opacity
        borderColor: statusConfig.color,
      };
    default:
      return {
        color: statusConfig.solidColor,
        backgroundColor: statusConfig.pastelColor,
        borderColor: statusConfig.color,
      };
  }
};
